import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, X, ArrowRight } from 'lucide-react';

/**
 * One-time "You're approved to host" banner.
 *
 * Rules (matches the approval ticket):
 *   - Renders only when user.role === 'host'
 *   - Reads dismissal flag from localStorage keyed by user_id so each
 *     approved host sees it exactly once across sessions.
 *   - Dismissing (X or Create Event click) persists the flag, so the
 *     banner never reappears on refresh / re-navigation.
 *   - Fails silently if user or localStorage is unavailable — never
 *     blocks the dashboard.
 */
export default function HostApprovalBanner({ user }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const storageKey = user?.user_id ? `vg_host_welcomed_${user.user_id}` : null;

  useEffect(() => {
    if (!user || user.role !== 'host' || !storageKey) return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) setVisible(true);
    } catch (_) { /* swallow — never block dashboard */ }
  }, [user, storageKey]);

  const markSeen = () => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, '1'); } catch (_) { /* swallow */ }
  };

  const handleDismiss = () => {
    markSeen();
    setVisible(false);
  };

  const handleCreateEvent = () => {
    markSeen();
    setVisible(false);
    navigate('/events/create');
  };

  if (!visible) return null;

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4"
      data-testid="host-approval-banner"
    >
      <div className="relative bg-gradient-to-r from-[#D4AF37]/10 via-[#D4AF37]/5 to-transparent border border-[#D4AF37]/30 rounded-sm p-4 sm:p-5">
        <button
          onClick={handleDismiss}
          className="absolute top-2.5 right-2.5 text-[#777] hover:text-[#EAE7E1] transition-colors p-1"
          aria-label="Dismiss"
          data-testid="host-approval-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pr-6">
          <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm sm:text-base font-bold text-[#EAE7E1]" data-testid="host-approval-title">You're approved to host</div>
              <p className="text-xs sm:text-sm text-[#AFAFAF] mt-0.5" data-testid="host-approval-body">Your host account is now active. You can now create and manage events.</p>
            </div>
          </div>
          <button
            onClick={handleCreateEvent}
            className="inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] font-bold text-xs sm:text-sm px-4 py-2 rounded-sm transition-opacity flex-shrink-0"
            data-testid="host-approval-cta"
          >
            Create Event <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
